import { Capacitor } from '@capacitor/core';
import realmToShard from './realm-shard-mapper';
import realmToDomain from './realm-domain-mapper';
import realmTopDomain from './realm-top-domain-mapper';
import { getBrowserRealm } from './browser-realm';
import { createSupportLink } from './support';
import { countryLocale, Realm, timezonesByRealm } from './constants';
import { getPersistedRealm, persistSelectedRealm } from './store';

let overrideEnv: string;
export const hostname = (): string => window.location.hostname;
export const environment = () => {
  if (overrideEnv) {
    return overrideEnv;
  }
  if (process.env.ENVIRONMENT) {
    return process.env.ENVIRONMENT;
  }
  if (process.env.VUE_APP_ENVIRONMENT) {
    return process.env.VUE_APP_ENVIRONMENT;
  }
  return process.env.VITE_ENVIRONMENT;
};

export const isNative = (): boolean => Capacitor.isNativePlatform();
export const isMobileNative = (): boolean => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android';
};
export const isLocal = (): boolean =>
  environment()?.startsWith('dev') || (hostname().startsWith('localhost') && !isNative());
export const isStaging = (): boolean => environment() === 'staging';
export const isProd = (): boolean => environment() === 'production';

class RealmNotFoundError extends Error {
  constructor() {
    super(`Realm not selected`);
  }
}

const useConfigHelper = () => {
  /**
   * Get the realm, in the following order of preference:
   * 1. Persisted realm - the one that was selected by the user
   * 2. Browser realm - the realm that was detected by the browser, if we are on a web app
   * 3. If we are on a native app and no selection has been made yet, we return null so we can prompt the user to select a realm
   *
   * ⚠️ Important - every mobile app that supports a single realm should call setRealm as soon as possible.
   * ⚠️ Imporatnt - Every mobile app that supports multiple realms should call getOptionalRealm as soon as possible,
   * and if it returns null, prompt the user to select a realm and then call setRealm with the selected realm.
   *
   * @throws {Error} If no realm has been selected.
   */
  const getRealm = (): Realm => {
    // If we have a persisted realm, we return it
    const persistedRealmState = getPersistedRealm();
    if (persistedRealmState) {
      return persistedRealmState.selectedRealm as Realm;
    }

    if (!isNative()) {
      const realm = getBrowserRealm();
      setRealm(realm);
      return realm;
    }

    // Throw if we are on a native app and no realm has been selected.
    // Mobile apps that support multiple realms should check if a realm has been selected using getOptinalRealm
    throw new RealmNotFoundError();
  };

  /**
   * Use this to check if a realm has been selected.
   *
   * Mobile apps that support a single realm should call setRealm as early as possible and then use getRealm instead.
   */
  const getOptionalRealm = (): Realm | null => {
    try {
      return getRealm();
    } catch (e) {
      if (e instanceof RealmNotFoundError) {
        return null;
      }
      throw e;
    }
  };

  const setRealm = (newRealm: Realm): void => {
    persistSelectedRealm(newRealm);
  };

  const getShard = (): string => realmToShard(getRealm());

  const getLocale = (): string => countryLocale[getRealm()];

  const getTimeZone = (): string => timezonesByRealm[getRealm()];

  const getUrl = (serviceName: string, withoutProtocol?: boolean, predefinedDomain?: string): string => {
    const proto = withoutProtocol ? '' : 'https://';
    let staging = isStaging();

    if (isLocal() && environment() !== 'production') {
      staging = true;
    }

    let domain = predefinedDomain;
    if (!predefinedDomain) {
      domain = realmToDomain(getRealm());
    }
    const subdomainSuffix = staging ? '-dev' : '';

    return `${proto}${serviceName}${subdomainSuffix}.${domain}`;
  };

  const getAppUrl = (appName: string, domain?: string): string => {
    if (domain) {
      switch (environment()) {
        case 'dev':
        case 'development':
        case 'staging':
          return `https://${appName}-dev.${domain}`;
        default:
          return `https://${appName}.${domain}`;
      }
    }
    const realm = getRealm();

    const tld = realmTopDomain[realm];

    let subDomain = 'app';
    let sld = appName;
    if (realm === 'fr' && appName === 'ilteducation') {
      subDomain = 'manage';
      sld = 'sondo';
    }

    switch (environment()) {
      case 'dev':
      case 'development':
      case 'staging':
        return `https://${subDomain}-dev.${sld}.${tld}`;
      default:
        return `https://${subDomain}.${sld}.${tld}`;
    }
  };

  /**
   * Resolve the base URL for an API.
   *
   * @param predefinedShard Pins the shard instead of deriving it from the realm — for apps
   * whose API lives on a single shard regardless of the domain they are served from
   * (e.g. an EU-only service on the .com domain, which would otherwise resolve to 'us').
   * Ignored when isGlobal is true.
   */
  const getApiUrl = (apiName: string, isGlobal?: boolean, predefinedShard?: 'us' | 'eu'): string => {
    const shard = isGlobal ? 'global' : predefinedShard || realmToShard(getRealm());

    switch (environment()) {
      case 'devlocal': // TODO remove legacy environment
      case 'dev':
      case 'development':
        return `https://api.${shard}.staging.ilteducation.com/${apiName}`;
      case 'staging':
        return `https://api.${shard}.staging.ilteducation.com/${apiName}`;
      default:
        return `https://api.${shard}.prod.ilteducation.com/${apiName}`;
    }
  };

  const setEnvironment = (env: string) => {
    overrideEnv = env;
  };

  const createQRCodeSupportLink = (customRealm?: Realm) => {
    const theRealm = customRealm || getRealm();
    let domain = realmToDomain(theRealm);
    if (theRealm === 'se') {
      domain = 'ilteducation.se';
    }
    const url = getUrl('support', false, domain);

    switch (theRealm) {
      case 'de':
        return createSupportLink(theRealm);
      case 'fi':
        return `${url}/kb/guide/fi/miten-luon-oppilaille-omat-tunnukset-myos-qr-koodi-zQwyUgfNj2/Steps/3688886`;
      case 'se':
        return `${url}/kb/guide/sv/logga-in-med-qr-kod-kom-igang-gGdNmTgTxF/Steps/3850324`;
      case 'fr':
      case 'no':
      case 'nl':
      case 'uk':
      case 'us':
        return createSupportLink(theRealm);
      default:
        return createSupportLink(theRealm);
    }
  };

  // Inverted mapping from TLD to realm
  const tldToRealmMap = Object.entries(realmTopDomain).reduce(
    (acc, [realm, tld]) => {
      acc[tld] = realm;
      return acc;
    },
    {} as Record<string, string>,
  );

  const getRealmFromHostname = (hostname: string): string | null => {
    if (!hostname) return null;

    // Sort TLDs by length (descending) to ensure we match 'co.uk' before 'uk'
    const sortedTlds = Object.keys(tldToRealmMap).sort((a, b) => b.length - a.length);

    for (const tld of sortedTlds) {
      if (hostname === tld || hostname.endsWith(`.${tld}`)) {
        return tldToRealmMap[tld];
      }
    }

    // default to last part (e.g. localhost) or return null
    return hostname.split('.').pop() || null;
  };

  return {
    createQRCodeSupportLink,
    getRealm,
    getOptionalRealm,
    getShard,
    getUrl,
    getAppUrl,
    getApiUrl,
    setEnvironment,
    getLocale,
    getTimeZone,
    getRealmFromHostname,
    setRealm,
  };
};

export default useConfigHelper;
